/**
 * NumberInputExamples.tsx
 * Comprehensive examples of HeroUI NumberInput component
 * 
 * Showcases all HeroUI NumberInput features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { NumberInput } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  DollarSign, 
  Percent,
  Calculator,
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  BarChart3
} from 'lucide-react';

export const NumberInputExamples: React.FC = () => {
  const [values, setValues] = useState<Record<string, number>>({
    disabled: 100,
    readonly: 250.50
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleValueChange = (key: string, value: number | undefined) => {
    if (value !== undefined) {
      setValues(prev => ({ ...prev, [key]: value }));
    } else {
      // Remove the key if value is undefined
      setValues(prev => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateRiskPercentage = (value: number | undefined): string => {
    if (value !== undefined) {
      if (value < 0) return 'Risk percentage cannot be negative';
      if (value > 10) return 'Risk percentage should not exceed 10%';
    }
    return '';
  };

  const handleRiskValidation = () => {
    const error = validateRiskPercentage(values['validated']);
    setErrors(prev => ({ ...prev, validated: error }));
  };

  // Helper function to get value props conditionally
  const getValueProps = (key: string) => {
    const value = values[key];
    return value !== undefined ? { value } : {};
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">NumberInput Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI NumberInput component with automatic theme integration and trading-specific examples
          </p>
        </div>

        {/* Basic Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">NumberInput Variants</h2>
              <Badge color="primary" variant="flat" size="sm">4 variants</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NumberInput
                variant="flat"
                label="Flat Variant"
                placeholder="0.00"
                {...getValueProps('flat')}
                onValueChange={(value) => handleValueChange('flat', value)}
                description="Flat input with subtle background"
              />
              
              <NumberInput
                variant="bordered"
                label="Bordered Variant"
                placeholder="0.00"
                {...getValueProps('bordered')}
                onValueChange={(value) => handleValueChange('bordered', value)}
                description="Input with visible border"
              />
              
              <NumberInput
                variant="underlined"
                label="Underlined Variant"
                placeholder="0.00"
                {...getValueProps('underlined')}
                onValueChange={(value) => handleValueChange('underlined', value)}
                description="Input with bottom border only"
              />
              
              <NumberInput
                variant="faded"
                label="Faded Variant"
                placeholder="0.00"
                {...getValueProps('faded')}
                onValueChange={(value) => handleValueChange('faded', value)}
                description="Faded appearance input"
              />
            </div>
          </CardBody>
        </Card>

        {/* Trading-Specific Examples */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Trading Examples</h2>
              <Badge color="success" variant="flat" size="sm">Real-world</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <NumberInput
                variant="bordered"
                label="Entry Price"
                placeholder="0.00"
                {...getValueProps('price')}
                onValueChange={(value) => handleValueChange('price', value)}
                startContent={<DollarSign className="w-4 h-4 text-default-400" />}
                step={0.01}
                minValue={0}
                formatOptions={{
                  style: 'currency',
                  currency: 'USD'
                }}
                color="primary"
              />
              
              <NumberInput
                variant="bordered"
                label="Quantity"
                placeholder="100"
                {...getValueProps('quantity')}
                onValueChange={(value) => handleValueChange('quantity', value)}
                startContent={<BarChart3 className="w-4 h-4 text-default-400" />}
                step={1}
                minValue={1}
                maxValue={10000}
                color="secondary"
              />
              
              <NumberInput
                variant="bordered"
                label="Risk Percentage"
                placeholder="2.0"
                {...getValueProps('percentage')}
                onValueChange={(value) => handleValueChange('percentage', value)}
                startContent={<Percent className="w-4 h-4 text-default-400" />}
                endContent={<span className="text-small text-default-400">%</span>}
                step={0.1}
                minValue={0}
                maxValue={10}
                color="warning"
              />
              
              <NumberInput
                variant="bordered"
                label="Stop Loss"
                placeholder="0.00"
                {...getValueProps('stopLoss')}
                onValueChange={(value) => handleValueChange('stopLoss', value)}
                startContent={<TrendingDown className="w-4 h-4 text-danger" />}
                step={0.01}
                minValue={0}
                color="danger"
                formatOptions={{
                  style: 'currency',
                  currency: 'USD'
                }}
              />
              
              <NumberInput
                variant="bordered"
                label="Take Profit"
                placeholder="0.00"
                {...getValueProps('takeProfit')}
                onValueChange={(value) => handleValueChange('takeProfit', value)}
                startContent={<TrendingUp className="w-4 h-4 text-success" />}
                step={0.01}
                minValue={0}
                color="success"
                formatOptions={{
                  style: 'currency',
                  currency: 'USD'
                }}
              />
              
              <NumberInput
                variant="bordered"
                label="Time Limit"
                placeholder="30"
                {...getValueProps('time')}
                onValueChange={(value) => handleValueChange('time', value)}
                startContent={<Clock className="w-4 h-4 text-default-400" />}
                endContent={<span className="text-small text-default-400">min</span>}
                step={5}
                minValue={5}
                maxValue={1440}
              />
            </div>
          </CardBody>
        </Card>

        {/* Content Slots */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Content Slots</h2>
              <Badge color="secondary" variant="flat" size="sm">Start & End</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NumberInput
                variant="bordered"
                label="With Start Content"
                placeholder="0.00"
                {...getValueProps('withStartContent')}
                onValueChange={(value) => handleValueChange('withStartContent', value)}
                startContent={<Calculator className="w-4 h-4 text-default-400" />}
              />
              
              <NumberInput
                variant="bordered"
                label="With End Content"
                placeholder="50"
                {...getValueProps('withEndContent')}
                onValueChange={(value) => handleValueChange('withEndContent', value)}
                endContent={<Percent className="w-4 h-4 text-default-400" />}
              />
              
              <NumberInput
                variant="bordered"
                label="Portfolio Value"
                placeholder="10000.00"
                {...getValueProps('withBothContent')}
                onValueChange={(value) => handleValueChange('withBothContent', value)}
                startContent={<DollarSign className="w-4 h-4 text-default-400" />}
                endContent={
                  <Button size="sm" variant="flat" className="h-6 min-w-unit-12">
                    Update
                  </Button>
                }
              />
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">NumberInput Sizes</h2>
              <Badge color="warning" variant="flat" size="sm">3 sizes</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <NumberInput
                size="sm"
                variant="bordered"
                label="Small"
                placeholder="0.00"
                {...getValueProps('small')}
                onValueChange={(value) => handleValueChange('small', value)}
              />
              
              <NumberInput
                size="md"
                variant="bordered"
                label="Medium"
                placeholder="0.00"
                {...getValueProps('medium')}
                onValueChange={(value) => handleValueChange('medium', value)}
              />
              
              <NumberInput
                size="lg"
                variant="bordered"
                label="Large"
                placeholder="0.00"
                {...getValueProps('large')}
                onValueChange={(value) => handleValueChange('large', value)}
              />
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">NumberInput Colors</h2>
              <Badge color="danger" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <NumberInput
                color="primary"
                variant="bordered"
                label="Primary"
                placeholder="0.00"
                {...getValueProps('primary')}
                onValueChange={(value) => handleValueChange('primary', value)}
              />
              
              <NumberInput
                color="secondary"
                variant="bordered"
                label="Secondary"
                placeholder="0.00"
                {...getValueProps('secondary')}
                onValueChange={(value) => handleValueChange('secondary', value)}
              />
              
              <NumberInput
                color="success"
                variant="bordered"
                label="Success"
                placeholder="0.00"
                {...getValueProps('success')}
                onValueChange={(value) => handleValueChange('success', value)}
              />
              
              <NumberInput
                color="warning"
                variant="bordered"
                label="Warning"
                placeholder="0.00"
                {...getValueProps('warning')}
                onValueChange={(value) => handleValueChange('warning', value)}
              />
              
              <NumberInput
                color="danger"
                variant="bordered"
                label="Danger"
                placeholder="0.00"
                {...getValueProps('danger')}
                onValueChange={(value) => handleValueChange('danger', value)}
              />
            </div>
          </CardBody>
        </Card>

        {/* States & Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">States & Features</h2>
              <Badge variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NumberInput
                variant="bordered"
                label="Required Field"
                placeholder="Enter required value"
                {...getValueProps('required')}
                onValueChange={(value) => handleValueChange('required', value)}
                isRequired
                color="primary"
                description="This field is required"
              />
              
              <NumberInput
                variant="bordered"
                label="Disabled Input"
                {...getValueProps('disabled')}
                onValueChange={(value) => handleValueChange('disabled', value)}
                isDisabled
                description="This input is disabled"
              />
              
              <NumberInput
                variant="bordered"
                label="Read-only Input"
                {...getValueProps('readonly')}
                onValueChange={(value) => handleValueChange('readonly', value)}
                isReadOnly
                description="This input is read-only"
                formatOptions={{
                  style: 'currency',
                  currency: 'USD'
                }}
              />
              
              <NumberInput
                variant="bordered"
                label="Min/Max Constraints"
                placeholder="1-100"
                {...getValueProps('minMax')}
                onValueChange={(value) => handleValueChange('minMax', value)}
                minValue={1}
                maxValue={100}
                description="Value must be between 1 and 100"
              />
            </div>
          </CardBody>
        </Card>

        {/* Advanced Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Advanced Features</h2>
              <Badge color="primary" variant="flat" size="sm">Stepper & Steps</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <NumberInput
                variant="bordered"
                label="Custom Step Size"
                placeholder="0.25"
                {...getValueProps('step')}
                onValueChange={(value) => handleValueChange('step', value)}
                step={0.25}
                minValue={0}
                description="Increments by 0.25"
              />
              
              <NumberInput
                variant="bordered"
                label="Hidden Stepper"
                placeholder="Enter manually"
                {...getValueProps('noStepper')}
                onValueChange={(value) => handleValueChange('noStepper', value)}
                hideStepper
                description="Manual input only"
              />
              
              <NumberInput
                variant="bordered"
                label="Currency Format"
                placeholder="0.00"
                {...getValueProps('currency')}
                onValueChange={(value) => handleValueChange('currency', value)}
                formatOptions={{
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }}
                startContent={<DollarSign className="w-4 h-4 text-default-400" />}
                description="Automatic currency formatting"
              />
            </div>
          </CardBody>
        </Card>

        {/* Validation Example */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Validation Example</h2>
              <Badge color="danger" variant="flat" size="sm">Real-time</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <NumberInput
                variant="bordered"
                label="Risk Percentage Validation"
                placeholder="2.0"
                {...getValueProps('validated')}
                onValueChange={(value) => handleValueChange('validated', value)}
                onBlur={handleRiskValidation}
                isInvalid={!!errors['validated']}
                errorMessage={errors['validated']}
                color={errors['validated'] ? "danger" : "default"}
                startContent={<Target className="w-4 h-4 text-default-400" />}
                endContent={<span className="text-small text-default-400">%</span>}
                minValue={0}
                maxValue={10}
                step={0.1}
                description="Risk percentage will be validated on blur (0-10%)"
              />
            </div>
          </CardBody>
        </Card>

        {/* Label Placements */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Label Placements</h2>
              <Badge color="secondary" variant="flat" size="sm">3 positions</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <NumberInput
                variant="bordered"
                label="Inside Label"
                placeholder="0.00"
                labelPlacement="inside"
                {...getValueProps('basic')}
                onValueChange={(value) => handleValueChange('basic', value)}
              />
              
              <NumberInput
                variant="bordered"
                label="Outside Label"
                placeholder="0.00"
                labelPlacement="outside"
                {...getValueProps('basic2')}
                onValueChange={(value) => handleValueChange('basic2', value)}
              />
              
              <NumberInput
                variant="bordered"
                label="Outside Left"
                placeholder="0.00"
                labelPlacement="outside-left"
                {...getValueProps('basic3')}
                onValueChange={(value) => handleValueChange('basic3', value)}
              />
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};